// Story 7.8 (Twitch) + Story 7.20 (YouTube) — "Linked accounts" section
// on the creator dashboard.
//
// Shows each provider's link state (linked-as vs not linked), the OAuth
// entry point (a plain anchor to the backend's /oauth/start which 302s
// to the provider — full page navigation, not fetch), and unlink. Also
// surfaces the ?<provider>=linked / ?<provider>=denied query param the
// OAuth callback redirects back with, as a one-shot toast.
//
// ONE ROW PER PROVIDER, driven by the table below. The v1 version was
// Twitch-shaped throughout; adding YouTube by copying it would have
// duplicated the toast effect, the hide rule and the unlink wiring.

import { useEffect } from "react";
import toast from "react-hot-toast";
import { useSearchParams } from "react-router-dom";

import { Button, Card, CardContent } from "@greenhold/components";

import { useLinkedAccounts, type LinkedAccountsResponse } from "./useLinkedAccounts";
import { useUnlinkAlias, type AliasProvider } from "./useUnlinkAlias";

interface ProviderConfig {
  key: AliasProvider;
  label: string;
  /** `creator_aliases.source_kind` the OAuth callback writes. */
  sourceKind: string;
  startUrl: string;
  /** What ingestion picks up — "clips", "Shorts". Both sentences below
   *  are built from this one noun so they cannot drift apart. */
  contentNoun: string;
  enabled: (data: LinkedAccountsResponse) => boolean;
  /** How the stored alias reads back. Twitch stores the login, so it
   *  gets an @; YouTube stores the immutable `UC…` channel id (a handle
   *  can be renamed or re-registered by someone else), which is not a
   *  handle and must not be dressed up as one. */
  formatAlias: (alias: string) => string;
}

const PROVIDERS: ProviderConfig[] = [
  {
    key: "twitch",
    label: "Twitch",
    sourceKind: "twitch_clip",
    startUrl: "/api/v1/creators/me/aliases/twitch/oauth/start",
    contentNoun: "clips",
    enabled: (d) => d.twitch_link_enabled,
    formatAlias: (alias) => `@${alias}`,
  },
  {
    key: "youtube",
    label: "YouTube",
    sourceKind: "youtube_short",
    startUrl: "/api/v1/creators/me/aliases/youtube/oauth/start",
    contentNoun: "Shorts",
    // Per-USER, not per-deployment: hidden outside the beta allowlist
    // while Google's app verification is pending.
    enabled: (d) => d.youtube_link_enabled === true,
    formatAlias: (alias) => alias,
  },
  {
    key: "kick",
    label: "Kick",
    sourceKind: "kick_clip",
    startUrl: "/api/v1/creators/me/aliases/kick/oauth/start",
    contentNoun: "clips",
    enabled: (d) => d.kick_link_enabled === true,
    // Kick stores the channel SLUG (its lookups take one), which reads
    // as a handle — unlike YouTube, where the alias is a raw channel id.
    formatAlias: (alias) => `@${alias}`,
  },
];

export function LinkedAccountsSection() {
  const linked = useLinkedAccounts();
  const [searchParams, setSearchParams] = useSearchParams();

  // One-shot callback-outcome toast (?<provider>=linked|denied).
  useEffect(() => {
    const hit = PROVIDERS.map((p) => ({
      provider: p,
      outcome: searchParams.get(p.key),
    })).find((x) => x.outcome);
    if (!hit || !hit.outcome) return;
    if (hit.outcome === "linked") {
      toast.success(`${hit.provider.label} account linked.`);
    } else if (hit.outcome === "denied") {
      toast.error(`${hit.provider.label} link was cancelled.`);
    }
    const next = new URLSearchParams(searchParams);
    next.delete(hit.provider.key);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const aliasFor = (sourceKind: string) =>
    linked.data?.data.find((a) => a.source_kind === sourceKind);

  // A provider is shown when the deployment offers it OR the creator
  // already has a link (from a previously-configured env — they must
  // still be able to unlink). When no provider qualifies, hide the whole
  // section rather than offer buttons that can't work. Wait for the
  // query to resolve to avoid a flash.
  const visible = linked.data
    ? PROVIDERS.filter(
        (p) => p.enabled(linked.data) || !!aliasFor(p.sourceKind),
      )
    : PROVIDERS;

  if (linked.data && visible.length === 0) {
    return null;
  }

  return (
    <section className="mt-10" data-testid="linked-accounts-section">
      <h2 className="text-xl font-heading font-bold mb-3">Linked accounts</h2>
      <Card>
        <CardContent className="p-6">
          {linked.isPending ? (
            <p className="opacity-70 text-sm">Loading…</p>
          ) : linked.isError ? (
            <p className="text-destructive text-sm">
              Couldn&apos;t load linked accounts.
            </p>
          ) : (
            <div className="space-y-4">
              {visible.map((provider) => (
                <ProviderLinkRow
                  key={provider.key}
                  provider={provider}
                  alias={aliasFor(provider.sourceKind)}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </section>
  );
}

// A child component rather than a loop body: each row owns a mutation
// hook, and hooks cannot be called from inside a map.
function ProviderLinkRow({
  provider,
  alias,
}: {
  provider: ProviderConfig;
  alias?: { alias: string; verified_at: string | null };
}) {
  const unlink = useUnlinkAlias(provider.key);

  return (
    <div className="flex flex-wrap items-center justify-between gap-4">
      <div>
        <p className="font-semibold m-0">{provider.label}</p>
        {alias ? (
          <p
            className="opacity-80 text-sm m-0"
            data-testid={`${provider.key}-linked-status`}
          >
            Linked as {provider.formatAlias(alias.alias)}
            {alias.verified_at ? " (verified)" : ""} — your{" "}
            {provider.contentNoun} will appear in the feed.
          </p>
        ) : (
          <p
            className="opacity-80 text-sm m-0"
            data-testid={`${provider.key}-unlinked-status`}
          >
            Not linked. Link your {provider.label} account to get your{" "}
            {provider.contentNoun} into the Greenhold feed.
          </p>
        )}
      </div>
      {alias ? (
        <Button
          type="button"
          variant="secondary"
          disabled={unlink.isPending}
          onClick={() =>
            unlink.mutate(undefined, {
              onSuccess: () =>
                toast.success(`${provider.label} account unlinked.`),
              onError: (e) => toast.error(e.message),
            })
          }
          data-testid={`${provider.key}-unlink-button`}
        >
          {unlink.isPending ? "Unlinking…" : "Unlink"}
        </Button>
      ) : (
        <Button asChild data-testid={`${provider.key}-link-button`}>
          {/* Full-page navigation — the backend 302s to the provider. */}
          <a href={provider.startUrl}>Link {provider.label} account</a>
        </Button>
      )}
    </div>
  );
}
